import type { TFunction } from 'i18next';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import { U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE } from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
const NS = 'u3101';

export function u3101GetEmployeeListColumns(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'codeEmployee',
      label: t('lblEmployee', { ns: NS }),
      widthClass: 'w-[175px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3101_FIND_EMPLOYEE,
        searchCriterionField: 'codeEmployee',
        valueKey: 'codeEmployee',
        displayKeys: ['codeEmployee', 'firstName', 'lastName'],
        selectedDisplayKeys: ['codeEmployee'],
        placeholder: t('phCriteriaEmployee', { ns: 'common' }),
      },
    },
    {
      name: 'numEmployee',
      label: t('lblNumEmployee', { ns: NS }),
      widthClass: 'w-[175px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'firstName',
      label: t('lblFirstName', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'lastName',
      label: t('lblLastName', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'remunerationType',
      label: t('lblRemunerationType', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        if (value === 'Salary') return t('valRemunerationTypeSalary', { ns: NS });
        if (value === 'Hourly') return t('valRemunerationTypeHourly', { ns: NS });
        return t('valRemunerationTypeHourly', { ns: NS });
      },
    },
    {
      name: 'hourlyBase',
      label: t('lblHourlyBase', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        if (value === null || value === undefined || value === '') return '';
        const n = typeof value === 'number' ? value : Number(value);
        return Number.isNaN(n) ? '' : n.toFixed(2);
      },
    },
    {
      name: 'email',
      label: t('lblEmail', { ns: NS }),
      widthClass: 'w-[300px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'phoneNumber',
      label: t('lblPhoneNumber', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'city',
      label: t('lblCity', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'vacationBncHours',
      label: t('lblVacationBncHours', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'holidayBncHours',
      label: t('lblHolidayBncHours', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'sickLeaveBncHours',
      label: t('lblSickLeaveBncHours', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'specialBncHours',
      label: t('lblSpecialBncHours', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'isActive',
      label: t('lblIsActive', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value, _row) => {
        return value ? (
          <span className="font-bold text-green-600">{t('valActive', { ns: NS })}</span>
        ) : (
          <span className="font-bold text-red-600">{t('valNotActive', { ns: NS })}</span>
        );
      },
    },
    {
      name: 'createdAt',
      label: t('lblFldCreatedAt', { ns: NS }),
      widthClass: 'w-[150px]',
      isDefaultColumn: true,
      type: 'date',
      inGridField: true,
      sortable: true,
      includeConditions: [
        'equals',
        'notequals',
        'greaterthan',
        'greaterthanorequal',
        'lesthan',
        'lesthanorequal',
        'interval',
      ],
      isCriteriaField: true,
    },
    {
      name: 'deletedAt',
      label: t('lblCndFieldDeletedAt', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'date',
      inGridField: true,
      align: 'center',
      display: (value) => {
        return value ? <span className="font-bold text-red-600">DELETED</span> : '';
      },
      includeConditions: ['isNotNull'],
      ovverideLabelConditions: [
        {
          name: 'isNotNull',
          label: t('lblCndDeletedAt', { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}
